import ctl from '@netlify/classnames-template-literals'
import { motion } from 'motion/react'
import {
	CSSProperties,
	MouseEvent as ReactMouseEvent,
	ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react'
import { logger } from '../../utilities'
import { simpleWipe } from '../animation'
import { Glyphs } from '../glyphs'
import { JobiLoader } from '../jobiloader'

const DEFAULT_PANE_WIDTH = 200
const MIN_PANE_WIDTH = 160
const MAX_PANE_WIDTH = 480
const STORAGE_PREFIX = 'library-layout-pane-width:'

export interface LibraryLayoutProps {
	children: ReactNode
	toolbar?: ReactNode
	committalBar?: ReactNode
	showCloseButton?: boolean
	onClose?: () => void
	sidebar?: ReactNode
	title?: ReactNode
	details?: ReactNode

	isLoading?: boolean

	defaultSidebarWidth?: number
	sidebarStorageKey?: string
	minSidebarWidth?: number

	defaultListWidth?: number
	listStorageKey?: string
	minListWidth?: number
}

function useResizablePane(storageKey: string | undefined, defaultWidth: number, minWidth: number) {
	const [width, setWidth] = useState<number>(() => {
		const clampedDefault = Math.min(MAX_PANE_WIDTH, Math.max(minWidth, defaultWidth))
		if (!storageKey || typeof window === 'undefined') { return clampedDefault }
		let stored: string | null
		try {
			stored = window.localStorage.getItem(STORAGE_PREFIX + storageKey)
		} catch (error) {
			logger.warn('LibraryLayout: localStorage read failed, using default width', {
				data: { storageKey, error: String(error) },
			})
			return clampedDefault
		}
		if (!stored) { return clampedDefault }
		const parsed = Number(stored)
		if (!Number.isFinite(parsed)) {
			logger.warn('LibraryLayout: invalid stored pane width, falling back to default', {
				data: { storageKey, stored },
			})
			return clampedDefault
		}
		return Math.min(MAX_PANE_WIDTH, Math.max(minWidth, parsed))
	})

	const widthRef = useRef(width)
	widthRef.current = width
	const minRef = useRef(minWidth)
	minRef.current = minWidth
	const dragAbortRef = useRef<AbortController | null>(null)

	const handleResizeStart = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
		event.preventDefault()
		const controller = new AbortController()
		dragAbortRef.current = controller

		const startX = event.clientX
		const startWidth = widthRef.current

		const handleMove = (moveEvent: MouseEvent) => {
			const delta = moveEvent.clientX - startX
			const next = Math.min(MAX_PANE_WIDTH, Math.max(minRef.current, startWidth + delta))
			setWidth(next)
		}

		const handleUp = () => {
			controller.abort()
			dragAbortRef.current = null
			document.body.style.cursor = ''
			document.body.style.userSelect = ''
			if (storageKey) {
				try {
					window.localStorage.setItem(STORAGE_PREFIX + storageKey, String(widthRef.current))
				} catch {
					// localStorage may be unavailable (private mode, quota) — drop persistence silently
				}
			}
		}

		window.addEventListener('mousemove', handleMove, { signal: controller.signal })
		window.addEventListener('mouseup', handleUp, { signal: controller.signal })
		document.body.style.cursor = 'col-resize'
		document.body.style.userSelect = 'none'
	}, [])

	useEffect(() => {
		return () => {
			if (!dragAbortRef.current) { return }
			dragAbortRef.current.abort()
			dragAbortRef.current = null
			document.body.style.cursor = ''
			document.body.style.userSelect = ''
		}
	}, [])

	return { width, handleResizeStart }
}

const resizeHandleClass = ctl(`
	absolute top-0 bottom-0 right-0 w-1.5 -mr-[3px] z-10
	cursor-col-resize
	hover:bg-blue-400/40 active:bg-blue-500/60 transition-colors
`)

function ResizeHandle({ onMouseDown }: { onMouseDown: (e: ReactMouseEvent<HTMLDivElement>) => void }) {
	return (
		<div
			role="separator"
			aria-orientation="vertical"
			onMouseDown={onMouseDown}
			className={resizeHandleClass}
		/>
	)
}

export function LibraryLayout(
	{
		children,
		toolbar,
		committalBar,
		showCloseButton,
		onClose,
		title,
		sidebar,
		details,
		isLoading = false,
		defaultSidebarWidth = DEFAULT_PANE_WIDTH,
		sidebarStorageKey,
		minSidebarWidth = MIN_PANE_WIDTH,
		defaultListWidth = DEFAULT_PANE_WIDTH,
		listStorageKey,
		minListWidth = MIN_PANE_WIDTH,
	}: LibraryLayoutProps,
) {
	const sidebarPane = useResizablePane(sidebarStorageKey, defaultSidebarWidth, minSidebarWidth)
	const listPane = useResizablePane(listStorageKey, defaultListWidth, minListWidth)

	const gridTemplateColumns = [
		sidebar ? `${sidebarPane.width}px` : null,
		details ? `${listPane.width}px` : 'minmax(0,1fr)',
		details ? 'minmax(0,1fr)' : null,
	].filter(Boolean).join(' ')

	return (
		<div
			className={ctl(`
				grid grid-rows-[auto_1fr_auto]
				relative h-full
			`)}
			style={{
				'--loading-opacity': isLoading ? '0' : '1',
				gridTemplateColumns,
			} as CSSProperties}
		>
			{isLoading && (
				<div className="absolute inset-0 bg-white/50 flex items-center justify-center">
					<JobiLoader />
				</div>
			)}
			<div
				className={ctl(`
					transition-all opacity-[var(--loading-opacity)]
					min-h-14 border-b border-grey-100 flex justify-between items-center col-span-full
				`)}
			>
				{title && (
					<div className="overflow-hidden block whitespace-nowrap text-lg font-medium px-4 text-grey-900 tracking-loose">
						<motion.div
							{...simpleWipe()}
							transition={{
								duration: 0.5,
								type: 'spring',
								delay: 0.2,
							}}
						>
							{title}
						</motion.div>
					</div>
				)}
				<div className="grid grow-1">{toolbar}</div>
				{showCloseButton && (
					<div className="self-stretch border-l border-grey-100">
						<button
							onClick={onClose}
							className={ctl(`
                            aspect-square cursor-pointer h-full
                            flex items-center justify-center
                            text-grey-400 hover:text-grey-600
                            group
                            `)}
						>
							<div className="group-hover:scale-110 transition-transform">
								<Glyphs.nova.CloseIcon />
							</div>
						</button>
					</div>
				)}
			</div>
			{sidebar && (
				<div className="transition-all opacity-[var(--loading-opacity)] border-r border-grey-100 relative">
					<div className="absolute inset-0 overflow-hidden">
						{sidebar}
					</div>
					<ResizeHandle onMouseDown={sidebarPane.handleResizeStart} />
				</div>
			)}
			<div
				className={ctl(`
					transition-all opacity-[var(--loading-opacity)]
					bg-grey-50 relative
				`)}
			>
				<div className="absolute inset-0 overflow-y-auto">
					{children}
				</div>
				{details && <ResizeHandle onMouseDown={listPane.handleResizeStart} />}
			</div>
			{details && (
				<div className="transition-all opacity-[var(--loading-opacity)] overflow-y-auto grid bg-white border-l border-grey-100">
					{details}
				</div>
			)}
			{committalBar && (
				<div className="transition-all opacity-[var(--loading-opacity)] grid col-span-full border-t border-grey-100">
					{committalBar}
				</div>
			)}
		</div>
	)
}
